import React, { useId } from 'react';
import { decodePeaks, downsamplePeaks, placeholderPeaks } from '../../utils/audioPeaks';
import { rangeSegments } from './voiceNoteModel';

/**
 * The bars of a voice note. Grey where it hasn't been heard, ink where it has: the ink
 * layer is the same bars clipped to the heard stretches, so a stretch heard twice and a
 * bar heard half-way both paint exactly as far as the clock got. `peaks` is the stored
 * string; with none yet it draws a quiet flat line that still fills as you listen.
 *
 * Pass `at` (the playhead, seconds) and it paints three states instead of two:
 *   ink    — heard, and behind the playhead (where you are)
 *   `ahead` — heard on an earlier listen, still ahead of the playhead
 *   `unheard` — never heard; a skipped stretch behind the playhead stays this colour too,
 *            so a gap you jumped over is obvious at a glance.
 */
export default function Waveform({ peaks, bars = 40, ranges = [], duration = 0, heard = '#000', unheard = '#D1D5DB', height = 24, gap = 0.45, minLevel = 0.1, at = null, ahead = '#8B929C', style }) {
  const id = useId();
  const levels = decodePeaks(peaks);
  const shown = levels ? downsamplePeaks(levels, bars) : placeholderPeaks(bars);
  const n = shown.length;
  const W = 1000;
  const H = 100;
  const slot = W / n;
  const bw = slot * (1 - gap);
  const rects = shown.map((v, i) => {
    const h = Math.max(minLevel, v) * H;
    return <rect key={i} x={i * slot + (slot - bw) / 2} y={(H - h) / 2} width={bw} height={h} rx={bw / 2} />;
  });
  const segments = rangeSegments(ranges, duration);
  // Split the heard stretches at the playhead: behind it is ink, ahead of it is the softer tone.
  const split = at != null && duration > 0;
  const headPct = split ? Math.min(100, Math.max(0, (at / duration) * 100)) : 100;
  const behind = split ? segments.map((s) => ({ left: s.left, width: Math.max(0, Math.min(s.left + s.width, headPct) - s.left) })).filter((s) => s.width > 0) : segments;
  const front = split ? segments.map((s) => { const l = Math.max(s.left, headPct); return { left: l, width: s.left + s.width - l }; }).filter((s) => s.width > 0) : [];
  const clip = (list) => list.map((s, i) => <rect key={i} x={(s.left / 100) * W} y={0} width={(s.width / 100) * W} height={H} />);
  return (
    <svg
      data-waveform
      data-measured={levels ? 'true' : 'false'}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden
      style={{ display: 'block', width: '100%', height, ...style }}
    >
      <defs>
        <clipPath id={`heard-${id}`}>{clip(behind)}</clipPath>
        {front.length > 0 && <clipPath id={`ahead-${id}`}>{clip(front)}</clipPath>}
      </defs>
      <g fill={unheard}>{rects}</g>
      {front.length > 0 && <g data-heard-ahead fill={ahead} clipPath={`url(#ahead-${id})`}>{rects}</g>}
      {behind.length > 0 && <g data-heard fill={heard} clipPath={`url(#heard-${id})`}>{rects}</g>}
    </svg>
  );
}
